import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Eye, EyeOff, Loader2, Mail, Lock, Building2, ShieldCheck,
  AlertCircle, KeyRound, LockKeyhole, Globe2,
} from "lucide-react";
import { toast } from "sonner";
import useAuthStore from "../store/authStore";
import api from "../services/api";
import Logo from "../components/Logo";
import ForgotPasswordDialog from "../components/auth/ForgotPasswordDialog";
import {
  validateEmail, validatePassword,
  loginErrorCopy, classifyLoginError, pickApiError,
} from "../utils/authValidators";

/**
 * B2BLogin — Iteration 64 (Phase 1 of B2B enterprise security)
 *
 * What's production-grade here vs the prior version:
 *   • Real client-side validation (email regex, password length, field-
 *     level errors), with autocomplete + name attributes for password
 *     managers to fill correctly.
 *   • B2B account gate — only `is_b2b === true` accounts are admitted
 *     through this portal. Anyone else is redirected with a clear
 *     "this isn't a B2B account → register/upgrade" CTA.
 *   • Friendly, typed error states (`invalid_credentials`,
 *     `rate_limited`, `ip_blocked`, `not_b2b`, `server_down`, ...) so
 *     the UI can show the right next-step.
 *   • Forgot password flow wired to /api/auth/password-reset/request.
 *   • Google sign-in (same JWT, same B2B gate).
 *   • Domain-aware capability lookup via /api/b2b/security/public-config:
 *     if the company has SSO required or 2FA required, the form will
 *     surface the right next-step (Phase 2/4 stubs).
 *   • Trust strip: TLS 1.3 · GST-compliant · ISO-track · SOC 2 ready
 *   • Accessibility — labels tied to inputs, `aria-label` on the eye
 *     toggle, `aria-live` for errors, focus management.
 *
 * NB: This file is the only B2B login surface; the regular `/login`
 * page is unchanged.
 */
export default function B2BLogin() {
  const navigate = useNavigate();
  const { login, loginWithGoogle, loading } = useAuthStore();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [fieldErr, setFieldErr] = useState({ email: "", password: "" });
  const [topError, setTopError] = useState(null); // { msg, kind }
  const [forgotOpen, setForgotOpen] = useState(false);
  const [companyCaps, setCompanyCaps] = useState(null); // {sso_required, sso_provider, two_fa_required}
  const debounceRef = useRef(null);
  const emailInputRef = useRef(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  // Lookup capability when the user has typed a plausible email so we can
  // surface SSO / 2FA cues *before* they hit submit.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!email || !email.includes("@")) {
      setCompanyCaps(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/b2b/security/public-config`, {
          params: { email: email.trim().toLowerCase() },
        });
        setCompanyCaps(data || null);
      } catch {
        setCompanyCaps(null);
      }
    }, 450);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [email]);

  const enforceB2BGate = (user) => {
    if (!user) return false;
    if (user.is_b2b === true) return true;
    setTopError({
      msg: "This account isn't a B2B account. Use the regular login, or register a B2B account.",
      kind: "not_b2b",
    });
    // Log them out aggressively — they shouldn't have a live B2B session
    useAuthStore.getState().logout().catch(() => {});
    return false;
  };

  const validate = () => {
    const e1 = validateEmail(email);
    const e2 = validatePassword(password, { min: 8 });
    setFieldErr({ email: e1 || "", password: e2 || "" });
    return !e1 && !e2;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTopError(null);
    if (!validate()) return;
    try {
      const user = await login(email.trim().toLowerCase(), password);
      if (!enforceB2BGate(user)) return;
      toast.success(`Welcome back${user.name ? `, ${user.name.split(" ")[0]}` : ""}.`);
      navigate("/dashboard/b2b", { replace: true });
    } catch (err) {
      const copy = loginErrorCopy(err);
      setTopError(copy);
    }
  };

  const handleGoogle = async (credentialResponse) => {
    setTopError(null);
    try {
      const user = await loginWithGoogle(credentialResponse.credential);
      if (!enforceB2BGate(user)) return;
      toast.success("Signed in with Google.");
      navigate("/dashboard/b2b", { replace: true });
    } catch (err) {
      setTopError({ msg: pickApiError(err, "Google sign-in failed."), kind: classifyLoginError(err?.response?.status, "") });
    }
  };

  const showSSOHint = companyCaps?.sso_required;
  const show2FAHint = companyCaps?.two_fa_required;

  return (
    <div
      className="min-h-screen bg-[#0B132B] flex items-stretch"
      data-testid="b2b-login-page"
    >
      {/* Left enterprise marketing panel (hidden on small screens) */}
      <aside className="hidden lg:flex flex-col justify-between w-1/2 p-12 xl:p-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/10 via-transparent to-transparent pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#FF6B00]/5 blur-3xl pointer-events-none" />
        <div className="relative">
          <Link to="/b2b" className="inline-block">
            <Logo size="lg" textColor="text-white" />
          </Link>
          <div className="inline-flex items-center gap-2 bg-[#FF6B00]/15 text-[#FF6B00] px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider mt-8">
            <Building2 className="h-3.5 w-3.5" /> B2B Business Portal
          </div>
          <h1 className="font-['Outfit'] text-4xl xl:text-5xl font-bold text-white mt-6 leading-tight">
            Enterprise logistics,<br />signed in securely.
          </h1>
          <p className="text-slate-300 text-sm mt-4 max-w-md">
            Multi-city fulfilment with GST-compliant invoicing, hub-routed
            transit, and SLA-backed delivery — protected by enterprise-grade
            sign-in.
          </p>
        </div>
        <div className="relative space-y-5">
          <TrustRow icon={LockKeyhole} title="Transport encryption" body="TLS 1.3 in transit · AES-256 at rest" />
          <TrustRow icon={ShieldCheck} title="Enterprise access controls" body="Brute-force lockout, IP allow-list, audit log" />
          <TrustRow icon={Globe2} title="GST-compliant invoicing" body="Indian GSTIN-ready statements & API" />
          <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-800">
            SOC 2 framework in progress · ISO 27001 track
          </div>
        </div>
      </aside>

      {/* Right form panel */}
      <main className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-[#0B132B] lg:bg-white relative">
        <div className="relative w-full max-w-md">
          {/* Mobile-only header */}
          <div className="lg:hidden text-center mb-6">
            <Link to="/b2b" className="inline-block">
              <Logo size="lg" textColor="text-white" />
            </Link>
            <div className="inline-flex items-center gap-2 bg-[#FF6B00]/20 text-[#FF6B00] px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider mt-4">
              <Building2 className="h-3.5 w-3.5" /> B2B Business Portal
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-7 space-y-5" data-testid="b2b-login-form-card">
            <div>
              <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">Sign in to your B2B account</h2>
              <p className="text-xs text-slate-500 mt-1">Use your work email and company password.</p>
            </div>

            {topError && <TopError error={topError} />}

            {(showSSOHint || show2FAHint) && !topError && (
              <SecurityHint sso={showSSOHint} twofa={show2FAHint} provider={companyCaps?.sso_provider} />
            )}

            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on" noValidate data-testid="b2b-login-form">
              <div className="space-y-1.5">
                <Label htmlFor="b2b-email" className="text-sm font-medium text-slate-700">Work email</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="b2b-email"
                    ref={emailInputRef}
                    type="email"
                    name="email"
                    autoComplete="username"
                    inputMode="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (fieldErr.email) setFieldErr((f) => ({ ...f, email: "" })); }}
                    aria-invalid={!!fieldErr.email}
                    aria-describedby={fieldErr.email ? "b2b-email-err" : undefined}
                    required
                    className={`rounded-xl h-12 pl-10 border-slate-200 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 ${
                      fieldErr.email ? "border-red-300 focus:border-red-400" : ""
                    }`}
                    data-testid="b2b-login-email"
                  />
                </div>
                {fieldErr.email && (
                  <p id="b2b-email-err" className="text-xs text-red-600" data-testid="b2b-login-email-error">{fieldErr.email}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="b2b-password" className="text-sm font-medium text-slate-700">Password</Label>
                  <button
                    type="button"
                    onClick={() => setForgotOpen(true)}
                    className="text-xs font-semibold text-[#FF6B00] hover:underline"
                    data-testid="b2b-forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="b2b-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); if (fieldErr.password) setFieldErr((f) => ({ ...f, password: "" })); }}
                    aria-invalid={!!fieldErr.password}
                    aria-describedby={fieldErr.password ? "b2b-password-err" : undefined}
                    required
                    className={`rounded-xl h-12 pl-10 pr-10 border-slate-200 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 ${
                      fieldErr.password ? "border-red-300 focus:border-red-400" : ""
                    }`}
                    data-testid="b2b-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="b2b-login-password-toggle"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {fieldErr.password && (
                  <p id="b2b-password-err" className="text-xs text-red-600" data-testid="b2b-login-password-error">{fieldErr.password}</p>
                )}
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-600 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="rounded border-slate-300 text-[#FF6B00] focus:ring-[#FF6B00]/20"
                  data-testid="b2b-login-remember"
                />
                Keep me signed in on this device for 30 days
              </label>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-12 text-base font-semibold"
                data-testid="b2b-login-submit"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in to B2B Portal"}
              </Button>
            </form>

            {/* Google sign-in — same gate (B2B account only) */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-[11px] uppercase tracking-wider"><span className="bg-white px-2 text-slate-400">or</span></div>
            </div>

            <div className="flex justify-center" data-testid="b2b-google-signin">
              <GoogleLogin
                onSuccess={handleGoogle}
                onError={() => setTopError({ msg: "Google sign-in cancelled or failed.", kind: "unknown" })}
                useOneTap={false}
                theme="outline"
                size="large"
                shape="rectangular"
                text="signin_with"
                width={320}
              />
            </div>

            <div className="text-center pt-2 space-y-1">
              <p className="text-sm text-slate-500">
                Don't have a B2B account?{" "}
                <Link to="/b2b/register" className="text-[#FF6B00] font-semibold hover:underline" data-testid="b2b-goto-register">
                  Register here
                </Link>
              </p>
              <p className="text-xs text-slate-400">
                Already a B2C customer?{" "}
                <Link to="/login" className="text-slate-600 font-medium hover:underline">Use the regular login</Link>{" "}and upgrade to B2B.
              </p>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 lg:text-slate-500 text-center mt-4">
            Protected by brute-force lockout and audit logging.{" "}
            <Link to="/privacy" className="underline hover:text-slate-600">Privacy</Link>
            {" · "}
            <Link to="/terms" className="underline hover:text-slate-600">Terms</Link>
          </p>
        </div>
      </main>

      <ForgotPasswordDialog
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        defaultEmail={email}
        variant="b2b"
      />
    </div>
  );
}

function TopError({ error }) {
  const { msg, kind } = error || {};
  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5 rounded-xl"
      data-testid="b2b-login-error"
      data-error-kind={kind || "unknown"}
    >
      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="flex-1">
        <p className="font-medium leading-snug">{msg}</p>
        {kind === "not_b2b" && (
          <p className="text-[11px] text-red-600/80 mt-1">
            <Link to="/login" className="underline">Customer login</Link>
            {" · "}
            <Link to="/b2b/register" className="underline">Register a B2B account</Link>
          </p>
        )}
        {kind === "ip_blocked" && (
          <p className="text-[11px] text-red-600/80 mt-1">
            Ask your company admin to add this network to the allow-list, or use an approved office connection / VPN.
          </p>
        )}
        {kind === "rate_limited" && (
          <p className="text-[11px] text-red-600/80 mt-1">
            For account safety we pause sign-in attempts after several failures. Use{" "}
            <button type="button" className="underline" onClick={() => null}>password reset</button>{" "}
            if you've forgotten your password.
          </p>
        )}
      </div>
    </div>
  );
}

function SecurityHint({ sso, twofa, provider }) {
  return (
    <div
      className="flex items-start gap-2 bg-blue-50 border border-blue-100 text-blue-800 text-xs px-3 py-2 rounded-xl"
      data-testid="b2b-login-security-hint"
    >
      <KeyRound className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        {sso && (
          <p className="font-medium">
            This company uses {provider ? `${provider.toUpperCase()} SSO` : "single sign-on"} — password login may be limited.
          </p>
        )}
        {twofa && (
          <p className="mt-0.5">Two-factor verification will be required after your password (coming soon).</p>
        )}
      </div>
    </div>
  );
}

function TrustRow({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#FF6B00]" />
      </div>
      <div>
        <p className="text-sm font-semibold text-white leading-tight">{title}</p>
        <p className="text-xs text-slate-400 leading-snug">{body}</p>
      </div>
    </div>
  );
}
